// AdsPopup.jsx — route-first ad selection with random fallback
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIdle } from "../../providers/IdleProvider";
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, Dialog } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useSelector } from 'react-redux';
import { selectTourActive } from '../../features/tour/tourSlice';
import ads from "./ads.json";

const ADS_BY_ROUTE = ads
const ADS_LIST = Object.values(ADS_BY_ROUTE)
const BLACKLIST_ROUTES = ["/onboard", "/dashboard"]
const DEFAULT_WA_PHONE = "6282228886906"

export function AdsPopup({
    intervalMs = 5 * 60 * 1000,
    showWhen = "active",       // "always" | "active" | "idle"
    showWhenHidden = false,    // attempts while hidden (still throttled by browser)
    catchUpOnReturn = true,    // show on refocus if interval elapsed
    routeOnly = false,
    onOpen,
    onClose,
}) {
    const { idle } = useIdle();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [ad, setAd] = useState(null);
    const lastShownRef = useRef(Date.now());
    const intervalRef = useRef(null);
    const { t } = useTranslation()
    const tourActive = useSelector(selectTourActive);

    const isBlacklisted = useCallback(
        (route) =>
            BLACKLIST_ROUTES.some(
                (blocked) =>
                    route === blocked ||
                    route.startsWith(blocked + "/") ||  // includes children
                    route.startsWith(blocked + "?") ||
                    route.startsWith(blocked + "#")
            ),
        []
    );

    const ROUTE_KEYS = useMemo(
        () => Object.keys(ADS_BY_ROUTE).sort((a, b) => b.length - a.length), // longest-first
        []
    );

    const isPathPrefix = (base, path) => {
        if (!path.startsWith(base)) return false;
        const next = path.charAt(base.length);
        return next === "" || next === "/" || next === "?" || next === "#";
    };

    const pickRandomAd = useCallback(() => {
        if (!ADS_LIST.length) return null;
        const idx = Math.floor(Math.random() * ADS_LIST.length);
        return ADS_LIST[idx];
    }, []);

    const pickAdForRoute = useCallback(
        (route) => {
            // 1) exact
            const exact = ADS_BY_ROUTE[route];
            if (exact) return exact;

            // 2) handles children paths
            const key = ROUTE_KEYS.find((k) => isPathPrefix(k, route));
            const prefixMatch = key ? ADS_BY_ROUTE[key] : null;

            if (routeOnly) return prefixMatch ?? null; // PREMIUM: only show on matched routes (incl. children)
            return prefixMatch ?? pickRandomAd();      // FREEMIUM: route-first (incl. children), else random
        },
        [ROUTE_KEYS, routeOnly, pickRandomAd]
    );

    const shouldShow = useCallback(() => {
        if (!showWhenHidden && document.visibilityState !== "visible") return false;
        if (showWhen === "always") return true;
        if (showWhen === "active") return !idle;
        if (showWhen === "idle") return idle;
        return false;
    }, [idle, showWhen, showWhenHidden]);

    const openAd = useCallback(() => {
        if (tourActive) return false;
        if (isBlacklisted(pathname)) return;

        const chosen = pickAdForRoute(pathname);
        if (!chosen) return;

        setAd(chosen);
        if (!open) setOpen(true);
        lastShownRef.current = Date.now();
        onOpen?.();
    }, [open, pathname, pickAdForRoute, isBlacklisted, onOpen, tourActive]);

    const tick = useCallback(() => {
        if (shouldShow()) openAd();
    }, [shouldShow, openAd]);

    useEffect(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(tick, intervalMs);
        tick();
        return () => clearInterval(intervalRef.current);
    }, [tick, intervalMs]);

    useEffect(() => {
        if (showWhen !== "idle") return;
        if (!idle) return;
        if (!showWhenHidden && document.visibilityState !== "visible") return;

        openAd()
    }, [idle, showWhen, showWhenHidden, openAd]);

    useEffect(() => {
        if (!catchUpOnReturn) return;
        const onVisible = () => {
            if (document.visibilityState !== "visible") return;
            const elapsed = Date.now() - lastShownRef.current;
            if (elapsed >= intervalMs && shouldShow()) openAd();
        };
        document.addEventListener("visibilitychange", onVisible, { passive: true });
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [catchUpOnReturn, intervalMs, shouldShow, openAd]);

    const handleClose = () => {
        setOpen(false);
        onClose?.();
    };

    const waHref = useMemo(() => {
        if (!ad) return "#";
        const phone = DEFAULT_WA_PHONE;
        const msg =
            ad.whatsappText ||
            `Hi Trucarbon, I want to upgrade to Premium.`;
        return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    }, [ad]);

    return (
        <Dialog
            aria-labelledby="ad-title"
            aria-describedby="ad-desc"
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { backgroundColor: "transparent", boxShadow: "none" } }}
        >
            {ad && (
                <Box
                    sx={{
                        p: 3,
                        mx: "auto",
                        maxWidth: 560,
                        borderRadius: 2,
                        border: "1px solid #E6F0EC",
                        boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
                        backgroundImage: "linear-gradient(180deg, #F5FBF9 0%, #FFFFFF 100%)",
                        textAlign: "center",
                        position: "relative",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                bgcolor: "rgba(10,86,75,0.08)",
                                color: "#0A564B",
                                textTransform: "uppercase",
                                letterSpacing: 0.3,
                            }}
                        >
                            {t('ads.badge')}
                        </Box>

                        <Button
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: "#6B7280",
                                textTransform: "none",
                                fontSize: "0.8rem",
                                minWidth: "auto",
                                "&:hover": { color: "#111827", background: "transparent" },
                            }}
                        >
                            ✕ {t('ads.close')}
                        </Button>
                    </Box>

                    {ad.image && (
                        <Box sx={{ px: 2, mt: 0.75 }}>
                            <Box
                                sx={{
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={ad.image}
                                    alt={ad.title}
                                    loading="lazy"
                                    sx={{
                                        display: "block",
                                        width: "100%",
                                        height: "auto",
                                        maxHeight: 220,
                                        objectFit: "cover",
                                        objectPosition: ad.objectPosition || "center center",
                                        bgcolor: "#F2F7F5",
                                    }}
                                />
                            </Box>
                        </Box>
                    )}

                    <Box
                        component="h2"
                        sx={{
                            mt: 1.5,
                            mb: 1,
                            fontSize: "1.5rem",
                            fontWeight: 800,
                            lineHeight: 1.3,
                            color: "#0A564B",
                        }}
                    >
                        {t(ad.title)}
                    </Box>

                    <Box
                        component="p"
                        sx={{
                            mx: "auto",
                            maxWidth: 520,
                            mb: 2,
                            fontSize: "0.95rem",
                            color: "#111827",
                            opacity: 0.95,
                            "& a": {
                                color: "#0A564B",
                                textDecoration: "underline",
                                textUnderlineOffset: 3,
                                textDecorationThickness: "1.5px",
                                fontWeight: 600,
                            },
                        }}
                    >
                        <Trans
                            i18nKey={ad.content}
                            components={{ a: <a target="_blank" rel="noreferrer" /> }}
                        />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
                        <Button
                            component="a"
                            href={waHref}
                            target="_blank"
                            rel="noreferrer"
                            startIcon={<WhatsAppIcon fontSize="small" />}
                            variant="contained"
                            color="primary"
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                py: 1.1,
                                fontWeight: 600,
                                textTransform: "none",
                                transition: "background-color 0.2s ease",
                                "&:hover": {
                                    backgroundColor: "secondary.main",
                                    color: "#fff",
                                },
                            }}
                        >
                            {t('ads.cta')}
                        </Button>
                    </Box>
                </Box>
            )}
        </Dialog>
    );
}
